// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Materials' barcode rules, in the browser (`app/domains/bom/barcode_rules.py`).
 *
 * A rule is a regular expression for a material code, shared by every line of that
 * material. A serial passes when any of its material's rules matches the whole normalized
 * serial; a material with none accepts anything. Checked here as a scan lands so the
 * operator is stopped with the part in hand; the server checks again regardless.
 *
 * Rules are written from an example: `patternFromExample` turns a serial read off a real
 * label into a rule the administrator can then loosen or tighten by hand.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/api/endpoints";
import type { BarcodeRule } from "@/api/types";
import { normalizeSerial } from "./serials";

export const PATTERN_MAX_LEN = 200;

/**
 * A rule generated from one example serial: each run of digits becomes "that many
 * digits", letters and punctuation stay as they are.
 *
 * `SN-A2024-00123` → `SN-A\d{4}-\d{5}`. Digits are what change from one part to the next
 * — dates, sequence numbers — while the letters are usually the supplier's fixed prefix.
 * Where a letter varies too, the generated rule is the place to start editing from.
 */
export function patternFromExample(example: string): string {
  const normalized = normalizeSerial(example);
  return normalized.replace(/\d+|[^\d]/g, (run) => {
    if (/^\d/.test(run)) return run.length === 1 ? "\\d" : `\\d{${run.length}}`;
    return run.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  });
}

/** The rule as a whole-serial matcher, or null when it is not a valid expression. */
export function compileRule(pattern: string): RegExp | null {
  if (!pattern || pattern.length > PATTERN_MAX_LEN) return null;
  try {
    return new RegExp(`^(?:${pattern})$`);
  } catch {
    return null;
  }
}

/** Whether a serial passes these rules: any one of them, or there are none. */
export function matchesAny(serial: string, patterns: readonly string[]): boolean {
  if (patterns.length === 0) return true;
  const normalized = normalizeSerial(serial);
  return patterns.some((pattern) => compileRule(pattern)?.test(normalized) ?? false);
}

export interface BarcodeRules {
  /** Every rule of the materials asked for, by material code. */
  byMaterial: Map<string, BarcodeRule[]>;
  /** The patterns of one material, none for a line without a code. */
  patternsOf: (materialCode: string | null | undefined) => string[];
  /** Whether a serial passes its material's rules. */
  accepts: (materialCode: string | null | undefined, serial: string) => boolean;
}

/** The rules of these materials, read once and shared by every screen that asks. */
export function useBarcodeRules(materialCodes: (string | null | undefined)[]): BarcodeRules {
  const codes = useMemo(
    () =>
      [...new Set(materialCodes.map((code) => code?.trim()).filter(Boolean) as string[])].sort(),
    [materialCodes],
  );
  const query = useQuery({
    queryKey: ["barcode-rules", codes],
    queryFn: () => api.barcodeRules.list(codes),
    enabled: codes.length > 0,
  });
  return useMemo(() => {
    const byMaterial = new Map<string, BarcodeRule[]>();
    for (const rule of query.data ?? []) {
      byMaterial.set(rule.material_code, [...(byMaterial.get(rule.material_code) ?? []), rule]);
    }
    const patternsOf = (materialCode: string | null | undefined) =>
      (materialCode ? (byMaterial.get(materialCode.trim()) ?? []) : []).map((rule) => rule.pattern);
    return {
      byMaterial,
      patternsOf,
      accepts: (materialCode, serial) => matchesAny(serial, patternsOf(materialCode)),
    };
  }, [query.data]);
}
